/**
 * Екрани платформи над справжнім API: список подання (E4a-W1) з метаданих `/meta`. Картка запису —
 * `/r/<сутність>/<id>` (routes/record.ts, E6a-W10); тут лише стара адреса `/x/…`, що веде на неї.
 *
 * Список — у першому екрані (performance-budget.md, розд. 3: «оболонка + стартовий список після
 * входу»): «/» після входу веде саме сюди, тож лінивий чанк тут був би водоспадом (метадані → код
 * списку → дані) і ще трьома файлами. Дані першої порції `loader` чекає разом із метаданими: один
 * рендер замість рендера на кожну відповідь (S4). Помилок не кидає — стани показує сам екран.
 */
import { createRoute, redirect } from '@tanstack/react-router';
import { taskViewKey } from '../../features/tasks/views.ts';
import { ListPage, ListSkeleton } from '../../platform/ListPage.tsx';
import { listParamsOf, platformListQuery } from '../../platform/list-source.tsx';
import { entityOf, platformMetaQuery } from '../../platform/meta.ts';
import { loadViewConfig } from '../../platform/view-config.ts';
import { decodeDraft, listSearch } from '../../platform/view-draft.ts';
import { opensAsBoard } from '../../platform/view-layout.tsx';
import { capabilitiesOf, getPrefs } from '../../prefs/prefs.ts';
import { rootRoute } from './root.tsx';

export const listRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/l/$view',
  // Незбережені зміни подання в адресі (`?d=`, FLT-08): розбір синхронний, до першого запиту.
  validateSearch: listSearch,
  loaderDeps: ({ search }) => ({ d: search.d }),
  loader: async ({ context, params, deps }) => {
    // Людина востаннє обрала «Воронку» для цієї сутності (KAN-01): подання відкривається дошкою.
    // Редірект — поза `try`: той ковтає все, а `redirect` маршрутизатор отримує як кинуте значення.
    let board = false;
    try {
      const meta = await context.queryClient.ensureQueryData(platformMetaQuery());
      const view = meta.views.find((v) => v.key === params.view);
      const entity = view && entityOf(meta, view.entity);
      if (!view || !entity) return;
      // Подання «Мої задачі» беруть дані самі (умова подання й `POST …/query`, E8a-W2): загальна
      // перша порція тут була б зайвим запитом без умови подання.
      if (taskViewKey(view, entity)) return;
      const prefs = getPrefs();
      board = opensAsBoard(entity, prefs.viewLayouts);
      if (!board) {
        // Той самий запит, що в екрана: особисті стовпці, сортування й умова з адреси (E4a-W2…W4).
        // Зіпсований `d` — чисте подання: екран скаже про це й прибере `d` з адреси. Умова подання —
        // з `start.config` чи `GET /views/{id}` (сервер її не підставляє). З чернеткою-умовою список
        // її не чекає, а вона вантажиться поруч: «Скинути» поверне подання без очікування.
        const { pageSize } = capabilitiesOf(prefs);
        const decoded = decodeDraft(deps.d);
        const draft = decoded.ok ? decoded.draft : {};
        const pending = loadViewConfig(context.queryClient, meta, view);
        pending.catch(() => undefined);
        const config = draft.filter !== undefined ? undefined : await pending;
        await context.queryClient.ensureInfiniteQueryData(
          platformListQuery(
            view,
            entity,
            pageSize,
            listParamsOf(view, entity, prefs, draft, config),
          ),
        );
      }
    } catch {
      // 401, збій, «зайнято» — стан області показує екран (повтор, вхід з поверненням).
    }
    if (board) {
      // Чернетка подання (`d`) — і на дошці: посилання списку з фільтром лишається тим самим.
      throw redirect({
        to: '/b/$view',
        params: { view: params.view },
        search: deps.d ? { d: deps.d } : {},
        replace: true,
      });
    }
  },
  pendingComponent: ListSkeleton,
  component: ListPage,
});

/**
 * Стара адреса картки S9 (`/x/…`): закладки, посилання в листах і нотатках. Веде на `/r/…` з тими
 * самими параметрами адреси, запис історії замінюється.
 */
export const legacyCardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/x/$entity/$recordId',
  beforeLoad: ({ params, location }) => {
    throw redirect({
      to: '/r/$entity/$recordId',
      params: { entity: params.entity, recordId: params.recordId },
      search: location.search,
      hash: location.hash,
      replace: true,
    });
  },
});
